// Analytics and Performance Tracking
import { cookieManager } from './cookieManager';

// Google Analytics 4 Configuration
export const GA_MEASUREMENT_ID = process.env.REACT_APP_GA_MEASUREMENT_ID || 'G-XXXXXXXXXX';

// Initialize Google Analytics
export const initializeAnalytics = () => {
  if (!cookieManager.getPreferences().analytics) return;

  // Load Google Analytics script
  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
  document.head.appendChild(script);

  // Initialize gtag
  window.dataLayer = window.dataLayer || [];
  function gtag(...args: any[]) {
    window.dataLayer.push(args);
  }
  window.gtag = gtag;

  gtag('js', new Date());
  gtag('config', GA_MEASUREMENT_ID, {
    page_title: document.title,
    page_location: window.location.href,
    anonymize_ip: true,
    cookie_flags: 'SameSite=None;Secure'
  });
};

// Track page views
export const trackPageView = (page: string, title?: string) => {
  if (!cookieManager.getPreferences().analytics) return;

  // Google Analytics
  if (typeof window.gtag !== 'undefined') {
    window.gtag('config', GA_MEASUREMENT_ID, {
      page_title: title || document.title,
      page_location: window.location.href,
      page_path: page
    });
  }

  // Custom analytics
  cookieManager.trackPageView(page);
};

// Track events
export const trackEvent = (eventName: string, parameters: any = {}) => {
  if (!cookieManager.getPreferences().analytics) return;

  // Google Analytics
  if (typeof window.gtag !== 'undefined') {
    window.gtag('event', eventName, parameters);
  }

  // Custom analytics
  cookieManager.trackEvent(eventName, parameters);
};

// Track user interactions
export const trackUserInteraction = (action: string, element: string, value?: any) => {
  trackEvent('user_interaction', {
    action,
    element,
    value,
    page: window.location.pathname,
    timestamp: Date.now()
  });
};

// Track form submissions
export const trackFormSubmission = (formName: string, success: boolean, errorMessage?: string) => {
  trackEvent('form_submission', {
    form_name: formName,
    success,
    error_message: errorMessage,
    page: window.location.pathname
  });
};

// Track button clicks
export const trackButtonClick = (buttonName: string, location: string) => {
  trackUserInteraction('click', 'button', { name: buttonName, location });
};

// Track link clicks
export const trackLinkClick = (linkText: string, destination: string) => {
  trackUserInteraction('click', 'link', { text: linkText, destination });
};

// Track scroll depth
export const trackScrollDepth = (depth: number) => {
  trackEvent('scroll_depth', {
    depth,
    page: window.location.pathname
  });
};

// Track time on page
export const trackTimeOnPage = (timeInSeconds: number) => {
  trackEvent('time_on_page', {
    time_seconds: timeInSeconds,
    page: window.location.pathname
  });
};

// Track conversion events
export const trackConversion = (conversionType: string, data?: any) => {
  trackEvent('conversion', {
    type: conversionType,
    ...data,
    page: window.location.pathname
  });
};

// Performance tracking
export const trackPerformance = () => {
  if (!cookieManager.getPreferences().performance) return;

  // Track Core Web Vitals
  if ('PerformanceObserver' in window) {
    // Largest Contentful Paint (LCP)
    new PerformanceObserver((list) => {
      const entries = list.getEntries();
      const lastEntry = entries[entries.length - 1];
      cookieManager.trackPerformance('lcp', lastEntry.startTime);
    }).observe({ entryTypes: ['largest-contentful-paint'] });

    // First Input Delay (FID)
    new PerformanceObserver((list) => {
      const entries = list.getEntries();
      entries.forEach((entry: any) => {
        if (entry.processingStart && entry.startTime) {
          cookieManager.trackPerformance('fid', entry.processingStart - entry.startTime);
        }
      });
    }).observe({ entryTypes: ['first-input'] });

    // Cumulative Layout Shift (CLS)
    let clsValue = 0;
    new PerformanceObserver((list) => {
      const entries = list.getEntries();
      entries.forEach((entry: any) => {
        if (!entry.hadRecentInput) {
          clsValue += entry.value;
        }
      });
      cookieManager.trackPerformance('cls', clsValue);
    }).observe({ entryTypes: ['layout-shift'] });
  }

  // Track page load time
  window.addEventListener('load', () => {
    const loadTime = performance.timing.loadEventEnd - performance.timing.navigationStart;
    cookieManager.trackPerformance('page_load_time', loadTime);
  });
};

// SEO tracking
export const trackSEOInteraction = (element: string, action: string) => {
  if (!cookieManager.getPreferences().analytics) return;

  cookieManager.trackSEOInteraction(element, action);
};

// Track search queries (if you have a search feature)
export const trackSearch = (query: string, results: number) => {
  trackEvent('search', {
    search_term: query,
    results_count: results,
    page: window.location.pathname
  });
};

// Track social media interactions
export const trackSocialInteraction = (platform: string, action: string) => {
  trackEvent('social_interaction', {
    platform,
    action,
    page: window.location.pathname
  });
};

// Track video interactions (if you have videos)
export const trackVideoInteraction = (videoId: string, action: string, time?: number) => {
  trackEvent('video_interaction', {
    video_id: videoId,
    action,
    time,
    page: window.location.pathname
  });
};

// Track file downloads
export const trackDownload = (fileName: string, fileType: string) => {
  trackEvent('file_download', {
    file_name: fileName,
    file_type: fileType,
    page: window.location.pathname
  });
};

// Track external link clicks
export const trackExternalLink = (url: string, linkText: string) => {
  trackEvent('external_link_click', {
    url,
    link_text: linkText,
    page: window.location.pathname
  });
};

// Track booking conversions
export const trackBookingStart = () => {
  trackConversion('booking_start');
};

export const trackBookingComplete = (packageType: string, value: number) => {
  trackConversion('booking_complete', {
    package_type: packageType,
    value
  });
};

// Track contact form interactions
export const trackContactFormStart = () => {
  trackEvent('contact_form_start', {
    page: window.location.pathname
  });
};

export const trackContactFormComplete = (formType: string) => {
  trackConversion('contact_form_complete', {
    form_type: formType
  });
};

// Initialize analytics when consent is given
export const initializeAnalyticsWithConsent = () => {
  if (cookieManager.hasConsent() && cookieManager.getPreferences().analytics) {
    initializeAnalytics();
    trackPerformance();
  }
};

// Type declarations for global gtag
declare global {
  interface Window {
    gtag: (...args: any[]) => void;
    dataLayer: any[];
  }
}
